<script setup>

import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxTextBox } from 'devextreme-vue/text-box'

import { useODataSource, useCurrencyColumnFormatter } from '@/composables/data-grid'
import { useProductAvailabilityColumn, usePriceColumn, useProductFamilyColumn, useSupplierBrandColumn, useVatTypeColumn } from '@/composables/data-grid-column'
import { codeNameDisplayExpr } from '@/composables/label'
import { useNotify } from '@/composables/notify'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import SupersessionProductDropDownBox from './SupersessionProductDropDownBox.vue'
import GridDropDownBox from '@/components/shared/GridDropDownBox.vue'

const i18n = getI18n()
const { notifyWarning } = useNotify()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()

webSiteStore.$onAction(({name, after }) => {
  if (name === 'onGridEditCanceled' && !props.options?.hideSupersession) {
    after(() => supersessionBtn.value?.hide())
  }
}) // Hook to hide supersession dropdown on grid edit cancel

const emit = defineEmits(['supersessionSelected', 'valueChanged', 'selectionClosed'])
const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  currency: {
    type: Object,
    default: null,
  },
  brand: {
    type: Object,
    default: null,
  },
  documentRowType: {
    type: Number,
  },
  purchaseFamilyId: {
    type: Number,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object,
    default: null
  },
})

const searchValue = ref('')

const onSearchValueChanged = (value) => {
  searchValue.value = value ?? ''
}

const productDataSource = computed(() => {
  const url = registryStore.api.getAbsoluteUrl() + registryStore.api.productsSearchUrl

  const ds = useODataSource(url, {
    searchExpr: ['code'],
  })

  const filters = []

  if (props.documentRowType != null) {
    filters.push(['product_type', '=', webSiteStore.mappingDocumentRowTypeToRegistryProductType[props.documentRowType]])
  }
  if (props.brand?.id) {
    filters.push(['supplier_brand_ref', '=', props.brand.id])
  }
  if (props.purchaseFamilyId != null) {
    filters.push(['purchase_family_id', '=', props.purchaseFamilyId])
  }

  if (props.documentRowType === webSiteStore.documentRowTypesEnum.EXPENSE && props.purchaseFamilyId == null) {
    const hiddenFamilyIds = [portalStore.hiddenPurchaseFamilyCodes.ROUNDING, portalStore.hiddenPurchaseFamilyCodes.EXPENSE_PERCENTAGE]
      .map(code => portalStore.purchaseFamilies.find(f => f.code === code)?.id)
      .filter(id => id != null)

    if (hiddenFamilyIds.length) {
      const notHiddenFilter = hiddenFamilyIds
        .map(id => ['purchase_family_id', '<>', id])
        .reduce((acc, current, idx) => idx === 0 ? current : [acc, 'and', current])

      filters.push([['purchase_family_id', '=', null], 'or', notHiddenFilter])
    }
  }

  let finalFilter = null

  if (filters.length === 1) {
    finalFilter = filters[0]
  } else if (filters.length > 1) {
    finalFilter = filters.reduce((acc, current, idx) => {
      if (idx === 0) return current
      return [acc, 'and', current]
    })
  }

  ds.filter(finalFilter)
  return ds
})

const supersessionId = ref(0)
const supersessionDataSource = ref([])

const onRowPrepared = (e) => {
  if (e.rowType === 'data') {
    if (e.data?.product_availability === registryStore.productAvailabilitiesEnum.NOT_AVAILABLE) {
      e.rowElement.classList.add('dx-disabled-row')
    }
    if (e.data?.product_ref === supersessionId.value) {
      e.rowElement.classList.add('dx-row-focused')
    }
  }
}

const onCellPrepared = (e) => {
  if ((e.column.dataField === 'has_supersession' && e.data?.has_supersession) || (e.column.dataField === 'product_has_supersession' && e.data?.product_has_supersession)) {
    e.cellElement.classList.add('dx-supersession-cell')
  }
}

const setNewValue = (e) => {
  props.data?.setValue(e)
  emit('valueChanged', { value: e })
}

const onValueChanged = async(e) => {
  if (e) {
    if (e.has_supersession) {
      notifyWarning(i18n.t('warning.product_has_supersession'))
    }
    if (e.product_availability === registryStore.productAvailabilitiesEnum.NOT_AVAILABLE) {
      notifyWarning(i18n.t('warning.product_not_available'))
    }
  }
  setNewValue(e)
}

const displayExpr = (obj) => {
  if (!obj) return ''
  supersessionId.value = obj.has_supersession ? obj.id : 0
  return codeNameDisplayExpr(obj)
}

const productGridColumns = computed(() =>
  [
    useSupplierBrandColumn({ sortOrder: searchValue.value ? undefined : 'asc', width: '5rem', codeOnly: true, allowSorting: false, allowFiltering: false }),
    { dataField: 'code', caption: i18n.t('common.code'), filterOperations: ['contains'], sortOrder: searchValue.value ? undefined : 'asc', width: '8rem', allowSorting: false, allowFiltering: false },
    { dataField: 'name', caption: i18n.t('common.description'), filterOperations: ['contains'], width: '20rem', allowSorting: false, allowFiltering: false },
    useProductFamilyColumn({ width: '6rem', allowSorting: false }),
    useProductAvailabilityColumn({ width: '6rem', allowSorting: false }),
    usePriceColumn({caption: i18n.t('common.priceNoVat'), width: '5rem', allowSorting: false, allowFiltering: false}),
    {
      dataField: 'cost', caption: i18n.t('common.cost'), width: '5rem',
      dataType: 'number',
      calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'cost', rowData.currency_id),
      calculateSortValue: 'cost',
      allowSorting: false, allowFiltering: false
    },
    useVatTypeColumn({ width: '6rem', allowSorting: false}),
    { dataField: 'has_supersession', caption: i18n.t('common.supersession'), width: '5rem', dataType: 'boolean', allowSorting: false, allowFiltering: false },
  ]
)

const onSupersessionClick = () => {
  if (props.options?.hideSupersession) {
    emit('supersessionSelected')
  } else if (supersessionId.value) {
    supersessionDataSource.value = useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.productSupersessionUrl(supersessionId.value), {
      key: 'product_ref',
    })
  }
}

const supersessionBtn = ref(null)
const gridDropDownBoxRef = ref(null)

const focus = () => {
  gridDropDownBoxRef.value?.focus()
}

defineExpose({ focus })
</script>
<template>
  <div>
    <span v-if="(props.documentRowType === webSiteStore.documentRowTypesEnum.TEXT || props.documentRowType === webSiteStore.documentRowTypesEnum.FREE) && !options.clipboard">
      <dx-text-box
        :disabled="props.disabled"
        :value="props.data?.data?.product_text"
        @value-changed="(e) => onValueChanged({ ...e.data, product_text: e.value })"
      />
    </span>
    <div
      v-else
      class="field-box-container"
    >
      <grid-drop-down-box
        ref="gridDropDownBoxRef"
        class="field-box"
        :disabled="props.disabled"
        :drop-down-options="{
          width: '70rem',
          height: '30rem'
        }"
        :open-on-field-click="false"
        :drop-down-display-expr="displayExpr"
        :value="props.data.value?.id || +(props.data.value) || null"
        :data-source="productDataSource"
        :columns="productGridColumns"
        :row-prepared="onRowPrepared"
        :cell-prepared="onCellPrepared"
        :search-timeout="500"
        :show-clear-button="true"
        :paging="{
          pageSize: 50,
          enabled: true
        }"
        @value-changed="onValueChanged"
        @search-value-changed="onSearchValueChanged"
        @selection-closed="emit('selectionClosed')"
      />
      <q-btn
        v-if="props.options?.hideSupersession"
        v-show="supersessionId"
        ref="supersessionBtn"
        class="field-box-btn"
        flat
        menu-self="top middle"
        icon="mdi-cached"
        @click="onSupersessionClick"
      />
      <q-btn-dropdown
        v-else
        v-show="supersessionId"
        ref="supersessionBtn"
        class="field-box-btn"
        flat
        menu-self="top middle"
        icon="mdi-cached"
        @click="onSupersessionClick"
      >
        <div class="supersession-container">
          <supersession-product-drop-down-box
            :data="{ supersession_id: supersessionId }"
            @new-product-selected="setNewValue"
          />
        </div>
      </q-btn-dropdown>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/quasar.variables.scss';
.field-box-container {
  display: flex;
  align-items: center;
  margin-right: 1rem;
  width: 100%;

}
.field-box {
  flex-grow: 1;
}

.field-box-btn {
  color: $warning;
}

.supersession-container {
  width: 70rem;
  height: 30rem;
  padding: 1rem;
  & .dx-widget {
    width: 100% !important;
  }
}
</style>
